import React, { useState, useEffect } from 'react'; // ✅ Adicione useEffect
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { z } from 'zod';

import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Alert from '../../components/ui/Alert';
import { useAuth } from '../../contexts/AuthContext';
import { ROTAS } from '@/routes/routePaths';

const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido.').max(255, 'Email muito longo.'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    resetarSenha,
    carregandoResetSenha, // ✅ Use o estado específico do contexto
    erroAutenticacao,
    limparErro
  } = useAuth();

  const [sucessoMensagem, setSucessoMensagem] = useState<string | null>(null);
  const [emailEnviado, setEmailEnviado] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const email = watch('email');

  // ✅ LIMPA ERROS AO MONTAR O COMPONENTE
  useEffect(() => {
    limparErro();
  }, [limparErro]);

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setSucessoMensagem(null);

    try {
      await resetarSenha(data.email);
      setEmailEnviado(true);
      setSucessoMensagem('Email de recuperação enviado com sucesso! Verifique sua caixa de entrada.');
    } catch (error) {
      // O erro já é tratado no AuthContext, não precisa fazer nada aqui
      console.log('Erro capturado no componente:', error);
    }
  };

  const reenviarEmail = async () => {
    if (!email) return;

    try {
      await resetarSenha(email);
      setSucessoMensagem('Email reenviado com sucesso!');
    } catch (error) {
      // Erro já tratado no contexto
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background principal da página - FIXADO NO FUNDO */}
      <div className="fixed inset-0 z-0">
        <img
          src="/Background-secao2.png"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20"></div>
      </div>


      {/* ✅ EXIBE ERROS DO CONTEXTO */}
      {erroAutenticacao && (
        <Alert
          tipo="error"
          mensagem={erroAutenticacao}
          onClose={limparErro}
          className="mb-4"
        />
      )}

      {sucessoMensagem && (
        <Alert
          tipo="success"
          mensagem={sucessoMensagem}
          onClose={() => setSucessoMensagem(null)}
          className="mb-4"
        />
      )}

      {/* Card de recuperação de senha */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 bg-background/95 backdrop-blur-sm p-8 rounded-lg shadow-2xl max-w-md w-full border border-primary overflow-hidden"
      >
        {/* Background interno do card */}
        <div className="absolute inset-0 z-0">
          <img
            src="/BG.png"
            alt="Background do card"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-black/5"></div>
        </div>

        {/* Conteúdo do card */}
        <div className="relative z-20 -mt-10">
          <div className="flex justify-center mb-6">
            <Link to={'/'}>
              <img
                src={'/clube.png'}
                alt="TugasCoins Logo"
                className='h-64 object-contain'
              />
            </Link>
          </div>

          <h2 className="text-3xl font-bold -mt-20 text-center text-primary mb-4">
            Recuperar Senha
          </h2>

          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </div>

            {!emailEnviado ? (
              <>
                <p className="text-text-light mb-4">
                  Digite seu email abaixo e enviaremos um link para redefinir sua senha.
                </p>
                <p className="text-text-light text-sm">
                  Não se preocupe, é rápido e fácil!
                </p>
              </>
            ) : (
              <>
                <p className="text-text-light mb-4">
                  <span className="text-primary font-semibold">Email enviado com sucesso!</span>
                </p>
                <p className="text-text-light text-sm mb-2">
                  Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                </p>
                <p className="text-text-light text-xs italic">
                  Não esqueça de verificar a pasta de spam!
                </p>
              </>
            )}
          </div>


          {!emailEnviado ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-text text-sm font-medium mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  tipo="email"
                  placeholder="Seu email cadastrado"
                  {...register('email')}
                  className="w-full"
                />
                {errors.email && (
                  <p className="text-danger text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <Button
                tipo="primary"
                type="submit"
                className="w-full py-3 text-lg font-semibold hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#cebc6e] hover:to-[#c5a663]"
                disabled={carregandoResetSenha} // ✅ Use o estado do contexto
              >
                {carregandoResetSenha ? 'Enviando...' : 'Enviar Link de Recuperação'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <Button
                tipo="primary"
                onClick={reenviarEmail}
                className="w-full py-3 text-lg font-semibold hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#cebc6e] hover:to-[#c5a663]"
                disabled={carregandoResetSenha} // ✅ Use o estado do contexto
              >
                {carregandoResetSenha ? 'Enviando...' : 'Reenviar Email'}
              </Button>

              <Button
                tipo="outline"
                onClick={() => navigate(ROTAS.LOGIN)}
                className="w-full py-3 text-lg font-semibold"
              >
                Voltar para o Login
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-text-light text-sm">
              Lembrou sua senha?{' '}
              <Link
                to={ROTAS.LOGIN}
                className="text-primary hover:text-accent font-medium"
              >
                Fazer Login
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <p className="text-text-light text-sm">
              Não tem uma conta?{' '}
              <Link
                to={ROTAS.CADASTRO}
                className="text-primary hover:text-accent font-medium"
              >
                Criar Conta
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;